import React, { useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Signup from '../components/SignIn';
import AuthContent from '../components/AuthContent';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { colors, typography, radius, btnPrimary } from '../theme';

const BENEFITS = {
    customer: [
        { icon: <QrCodeScannerIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Walk-In dengan Imbasan QR', description: 'Datang terus ke kedai, imbas kod QR untuk mendaftar dengan pantas, ketahui nombor giliran serta anggaran masa menunggu anda.' },
        { icon: <BookOnlineIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Tempahan Secara Dalam Talian', description: 'Tempah slot anda lebih awal melalui sistem atas talian yang mudah dan pantas, bila-bila masa dan di mana sahaja.' },
        { icon: <ThumbUpAltRoundedIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Barber Paling Disyorkan', description: 'Pilih barber yang paling dipercayai berdasarkan ulasan dan penilaian terbaik dari pelanggan sebenar.' },
        { icon: <AccessTimeFilledIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Jangkaan Masa Giliran', description: 'Ketahui jangkaan masa menunggu sebelum hadir agar anda boleh rancang masa dengan lebih bijak.' },
    ],
    owner: [
        { icon: <DashboardIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Dashboard Masa Nyata', description: 'Pantau giliran, temujanji dan status barber anda secara langsung tanpa perlu refresh.' },
        { icon: <PeopleIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Urus Pasukan Barber', description: 'Tambah barber baru dengan kod jemputan dan pantau prestasi mereka setiap hari.' },
        { icon: <QrCodeScannerIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Sistem Giliran QR', description: 'Pelanggan imbas QR untuk masuk giliran sendiri — tiada kaunter, tiada kesesakan.' },
        { icon: <BarChartIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Laporan & Statistik', description: 'Lihat jumlah tempahan, walk-in dan populariti setiap perkhidmatan anda.' },
    ],
    barber: [
        { icon: <ContentCutIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Dashboard Peribadi', description: 'Urus giliran dan temujanji anda sendiri dengan mudah — semua dalam satu paparan.' },
        { icon: <NotificationsActiveIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Status Ketersediaan', description: 'Togol status tersedia atau tidak tersedia pada bila-bila masa dengan satu ketukan.' },
        { icon: <CalendarMonthIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Jadual Harian', description: 'Lihat semua temujanji dan walk-in anda untuk hari ini dalam satu senarai yang tersusun.' },
        { icon: <ThumbUpAltRoundedIcon sx={{ fontSize: 28, color: colors.primary }} />, title: 'Bina Reputasi', description: 'Kumpul ulasan dan bina nama anda dalam komuniti barber tempatan.' },
    ],
};

const CTA_TEXT = {
    customer: { heading: 'Semua Yang Anda Perlukan',   sub: 'Dari tempahan hingga walk-in — semuanya dalam satu aplikasi yang mudah.',        btn: 'Mula Sekarang →' },
    owner:    { heading: 'Kenapa Pilih DigitalQ?',   sub: 'Platform lengkap untuk mengurus kedai barber anda dengan lebih cekap.',          btn: 'Daftar Kedai Anda →' },
    barber:   { heading: 'Kenapa Sertai DigitalQ?',  sub: 'Platform terbaik untuk barber profesional mengurus jadual dan pelanggan mereka.', btn: 'Masukkan Kod Jemputan →' },
};

const SignUpPage = () => {
    const [searchParams] = useSearchParams();
    const roleParam  = searchParams.get('role');
    const hasRole    = !!roleParam && ['owner', 'barber', 'customer'].includes(roleParam);
    const role       = hasRole ? roleParam : null;
    const authRole   = role === 'owner' ? 'owner' : role === 'barber' ? 'barber' : 'customer';
    const benefits   = BENEFITS[authRole];
    const cta        = CTA_TEXT[authRole];
    const benefitsRef = useRef(null);

    return (
        <Box sx={{ bgcolor: colors.bgGradient, minHeight: '100vh' }}>

            {/* ── Section 1: Auth ── */}
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 2, md: 6 }, py: 4, gap: { xs: 4, md: 8 }, flexDirection: { xs: 'column', md: 'row' }, position: 'relative' }}>
                {hasRole && (
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <AuthContent role={authRole} />
                    </Box>
                )}

                <Signup />

                {hasRole && (
                    <Box onClick={() => benefitsRef.current?.scrollIntoView({ behavior: 'smooth' })} sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'pointer', opacity: 0.6, transition: 'opacity .2s', '&:hover': { opacity: 1 } }}>
                        <Typography sx={{ fontSize: 11, color: colors.primary, letterSpacing: 1, textTransform: 'uppercase' }}>Why DigitalQ?</Typography>
                        <KeyboardArrowDownIcon sx={{ color: colors.primary, fontSize: 28, animation: 'bounce 1.5s infinite', '@keyframes bounce': { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(6px)' } } }} />
                    </Box>
                )}
            </Box>

            {/* ── Section 2: Benefits ── */}
            {hasRole && (
                <Box ref={benefitsRef} sx={{ px: { xs: 3, md: 8 }, py: { xs: 6, md: 8 }, borderTop: `1px solid ${colors.border}` }}>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: { xs: 22, md: 28 }, fontWeight: 700, color: colors.textPrimary, mb: 1 }}>
                            {cta.heading}
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: colors.textFaint, maxWidth: 480, mx: 'auto' }}>
                            {cta.sub}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, maxWidth: 900, mx: 'auto' }}>
                        {benefits.map((item, i) => (
                            <Box key={i} sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 3, transition: 'border-color .2s', '&:hover': { borderColor: colors.primaryBorder } }}>
                                <Box sx={{ width: 52, height: 52, bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                    {item.icon}
                                </Box>
                                <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary, mb: 1 }}>{item.title}</Typography>
                                <Typography sx={{ fontSize: 13, color: colors.textFaint, lineHeight: 1.7 }}>{item.description}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                        <Typography sx={{ fontSize: 13, color: colors.textFaintest, mb: 2 }}>Sudah bersedia?</Typography>
                        <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} variant="contained" sx={{ ...btnPrimary, fontSize: 14, px: 4, py: 1.2 }}>
                            {cta.btn}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default SignUpPage;